import { useEffect } from "react";
import { Bell, MessageCircle, Utensils } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useProfile } from "@/lib/api";
import AppStoreBadges from "@/components/AppStoreBadges";
import { isAndroid, isIOS, useDeviceStoreLink } from "@/lib/storeLinks";
import tracking from "@/lib/tracking";

const STEPS = [
  { Icon: Utensils, key: "dashboard.installApp.step1" },
  { Icon: MessageCircle, key: "dashboard.installApp.step2" },
  { Icon: Bell, key: "dashboard.installApp.step3" },
] as const;

/** Written out rather than interpolated, so the class names stay greppable. */
const CTA_CLASS = {
  primary: "btn-primary",
  secondary: "btn-secondary",
} as const;

interface InstallAppPitchProps {
  /** Tracking source, so each placement is distinguishable in analytics. */
  source: string;
  className?: string;
  /**
   * Weight of the store CTA. The dashboard banner is the only thing asking
   * for anything on its row, so it takes the solid button. On the booking
   * confirmation this sits above two more buttons, and three filled pills
   * stacked down a screen read as three competing demands — that placement
   * passes "secondary" so the whole column is outlined and the emphasis comes
   * from order instead.
   */
  ctaVariant?: "primary" | "secondary";
}

/**
 * The shared "why install the app" pitch — what lands in the app, ahead of the
 * dinner, plus a store CTA. Callers decide when to show it
 * (see useShouldPitchAppInstall) and supply the surrounding surface.
 */
export default function InstallAppPitch({
  source,
  className = "",
  ctaVariant = "primary",
}: InstallAppPitchProps) {
  const { t } = useT();
  const { data: profileData } = useProfile();
  const { url: storeUrl, store } = useDeviceStoreLink();

  const email: string | undefined = profileData?.data?.user?.email || undefined;

  // On a phone we know the store, so one button beats two badges; on desktop
  // the device is ambiguous, so both badges stay.
  const onPhone = isIOS() || isAndroid();

  useEffect(() => {
    tracking.installAppBannerShown(source);
  }, [source]);

  return (
    <div className={className}>
      <h3 className="text-base font-semibold text-neutral-800 dark:text-neutral-100">
        {t("dashboard.installApp.title")}
      </h3>
      {/* The reason, stated before the list of what. The title says what to do
          and the icon list says what is inside; neither said why it has to be
          the app, and push is the honest answer — every reminder before the
          night is delivered to a device token. */}
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        {t("dashboard.installApp.subtitle")}
      </p>

      {/* What happens next — the app is where each of these lands. */}
      <ul className="mt-3 space-y-2 text-sm text-neutral-600 dark:text-neutral-300">
        {STEPS.map(({ Icon, key }) => (
          <li key={key} className="flex gap-2.5">
            <Icon
              className="w-4 h-4 mt-0.5 shrink-0 text-primary-600 dark:text-primary-400"
              strokeWidth={2}
            />
            <span className="leading-snug">{t(key)}</span>
          </li>
        ))}
      </ul>

      {onPhone ? (
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          // Full-width on a phone; a normal pill if the viewport is wide.
          className={`btn ${CTA_CLASS[ctaVariant]} mt-4 block w-full sm:w-auto sm:inline-block sm:px-8 text-center py-2.5`}
          onClick={() =>
            store === "play_store"
              ? tracking.playStoreClicked(source)
              : tracking.appStoreClicked(source)
          }
        >
          {t("dashboard.installApp.cta")}
        </a>
      ) : (
        <AppStoreBadges className="mt-4" badgeHeight={40} source={source} />
      )}

      {/* Apple Sign-In users often create a second account in the app;
          naming the account's email steers them back to this one. */}
      {email && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3">
          {t("dashboard.installApp.signInAs")}{" "}
          <span className="font-semibold text-neutral-700 dark:text-neutral-200 break-words">
            {email}
          </span>
        </p>
      )}
    </div>
  );
}
